import {
    EventCardActions,
    EventCardBadge,
    EventCardDate,
    EventCardDetails,
    EventCardDetailsSpacer,
    EventCardLink,
    EventCardLocation,
    EventCardPrice,
    EventCardPromoCode,
    EventCardSaveAction,
    EventCardShareAction,
    EventCardTitle,
    EventCardTracking,
    EventCardUrgencySignals,
    getEventCardLinkProps,
    getFormattedSalesStatus,
    getShareActionProps,
    HORIZONTAL_LEFT_CARD_BADGE_STYLE,
    useFormattedLocationString,
    useGetFormattedEventDateTimeString,
    useMinPriceString,
    VerticalEventCard,
    VERTICAL_EVENT_CARD_IMAGE_CONFIG,
} from '@eventbrite/event-renderer';
import React from 'react';
import { EventCardExperimentalPromotedLabel } from '../../Experiments/EventCardExperimentalPromotedLabel';
import { EventCardMultiImage } from '../../Experiments/EventCardMultiImage';
import { DiscoverEventCardProps } from '../../types';
import './DiscoverVerticalSearchEventCard.scss';

export interface UtmOptions {
    'utm-campaign'?: string;
    'utm-content'?: string;
    'utm-medium'?: string;
    'utm-term'?: string;
    'utm-source'?: string;
    'utm-share-source'?: string;
}

const utmOptions: UtmOptions = {
    'utm-campaign': 'social',
    'utm-content': 'attendeeshare',
    'utm-medium': 'discovery',
    'utm-share-source': 'mobile-search-results',
};

export const DiscoverVerticalSearchEventCard: React.FunctionComponent<
    DiscoverEventCardProps
> = (props) => {
    const {
        event,
        locale,
        isRaised,
        statsigLocationString,
        moreActions,
        affCode = 'ebdssbdestsearch',
        'data-testid': dataTestId,
        isExpansionsLoading,
    } = props;
    const { urgencySignals } = event;

    const linkProps = getEventCardLinkProps({
        affCode: affCode,
        url: event?.url,
        isTargetBlank: true,
        isNoFollow: event.ticketsBy !== 'Eventbrite',
        onClick: (e) => props.onClick?.(e),
    });

    const formattedDateString = useGetFormattedEventDateTimeString({
        startDate: event?.startDate,
        startTime: event?.startTime,
        timezone: event?.timezone,
        repeatingInstanceCount: event?.repeatingInstanceCount,
        locale: locale,
        useUserTimezone: event.isOnlineEvent,
        dateTimeDelimeter: '•',
    });

    const formattedPriceRangeString = useMinPriceString({
        price: event?.minPrice,
        locale: locale,
    });

    const formattedLocationString = useFormattedLocationString({
        venue: event.venue,
        style: 'venue',
    });

    //Only show active Sales Status (sold out etc)
    //For non-repeating events to prevent incorrect signals.
    const formattedSalesStatus = getFormattedSalesStatus(event);

    const eventCardTrackingProps = {
        statsigLocationString: statsigLocationString,
        event,
    };

    return (
        <section
            className="discover-vertical-event-card"
            data-testid={dataTestId}
        >
            <VerticalEventCard actionVisibility="hidden" isRaised={isRaised}>
                <EventCardTracking {...eventCardTrackingProps}>
                    <EventCardBadge style={HORIZONTAL_LEFT_CARD_BADGE_STYLE}>
                        {formattedSalesStatus}
                    </EventCardBadge>
                    <EventCardMultiImage
                        event={event}
                        linkProps={linkProps}
                        isLazyImage={props.isLazyImage ? 'lazy' : 'eager'}
                        tokens={{
                            ...VERTICAL_EVENT_CARD_IMAGE_CONFIG,
                            '--image-background-color': event.edgeColor,
                        }}
                    />
                    <EventCardDetails
                        space="xxs-spacing"
                        tokens={{
                            '--EventCardDetailsPadding': '8px 8px 16px 12px',
                            '--EventCardDetailsFlexGrow': '2',
                            '--EventCardDetailsPosition': 'relative',
                        }}
                    >
                        {!formattedSalesStatus && (
                            <EventCardUrgencySignals
                                className="discover-vertical-event-card__urgency-signal"
                                urgencySignals={urgencySignals}
                            />
                        )}
                        <EventCardLink {...linkProps}>
                            <EventCardTitle
                                color="neutral-800"
                                className="event-card__clamp-line--two"
                                title={event.name}
                            />
                        </EventCardLink>
                        <EventCardDate
                            color="neutral-800"
                            formattedDateString={formattedDateString}
                            variant="body-md-bold"
                        />
                        <EventCardLocation
                            className="event-card__clamp-line--one"
                            formattedLocationString={formattedLocationString}
                            variant="body-md"
                        />
                        <EventCardDetailsSpacer />
                        <div className="discover-vertical-event-card__price-wrapper">
                            <EventCardPrice
                                color="neutral-800"
                                isLoading={isExpansionsLoading}
                                isFree={event.isFree}
                                formattedPriceString={formattedPriceRangeString}
                                variant="body-md-bold"
                            />
                            <EventCardPromoCode
                                openDiscount={event.openDiscount}
                                locale={locale}
                            />
                        </div>

                        {event.isPromoted && (
                            <EventCardExperimentalPromotedLabel />
                        )}

                        <EventCardActions
                            tokens={{
                                '--EventCardActionsPosition': 'absolute',
                                '--EventCardActionsTop': '-48px',
                                '--EventCardActionsRight': '8px',
                                '--EventCardActionsGap': '8px',
                            }}
                            actionStyleOverride="outline"
                        >
                            <EventCardSaveAction
                                isAuthenticated={!!props.isAuthenticated}
                                savedByYou={event.savedByYou}
                                id={event.id}
                                eventName={event.name}
                                imageUrl={event.imageUrl}
                            />
                            <EventCardShareAction
                                {...getShareActionProps({
                                    name: event.name,
                                    id: event.id,
                                    url: event.url,
                                    affCode: affCode,
                                    utmOptions: utmOptions,
                                })}
                            />
                            {moreActions}
                        </EventCardActions>
                    </EventCardDetails>
                </EventCardTracking>
            </VerticalEventCard>
        </section>
    );
};
